import {
  UilTrash,
  UilPen,
  UilUpload,
  UilImport,
  UilPlus,
} from '@iconscout/react-unicons';
import {
  Button,
  ButtonGroup,
  IconButton,
  Divider,
  ConfirmDialog,
  Notification,
} from '@xbotvn/mui';
import React, {
  useMemo, useState,
} from 'react';
import {
  useDispatch, useSelector,
} from 'react-redux';
import { v4 as uuid } from 'uuid';

import DropzoneModal from '../../../components/DropzoneModal';
import Table from '../../../components/Table';
import Waiting from '../../../components/Waiting';
import {
  MEMBERS,
} from '../../../libs/config';
import {
  cloneDeep,
  concat,
  filter,
  get,
  getOr,
  find,
  keyBy,
  values,
} from '../../../libs/lodash';
import {
  ROLES,
} from '../../../libs/utils';
import {
  FILE,
  UNIT,
} from '../../../redux/actions/constants';
import {
  importExcel,
} from '../Helpers';
import * as Styles from '../styles';

import MemberEditor from './MemberEditor';

export default function Member() {
  const dispatch = useDispatch();

  const {
    selectedUnit,
    handling,
    activeUnit,
  } = useSelector(({
    unit, user,
  }) => ({
    selectedUnit: get(['data', user.activeUnit], unit),
    handling: getOr(false, 'handling', unit),
    activeUnit: user.activeUnit,
  }));

  const [memberImport, setMemberImport] = useState();
  const [memberUpdate, setMemberUpdate] = useState();
  const [showImport, setShowImport] = useState();
  const [removeConfirm, setRemoveConfirm] = useState(false);

  const columns = useMemo(() => [
    {
      Header: 'STT',
      id: 'stt',
      disableFilters: true,
      style: {
        width: 200,
      },
      accessor: (_, index) => index + 1,
    },
    {
      Header: 'Họ và tên',
      accessor: 'name',
      style: {
        width: 500,
      },
    },
    {
      Header: 'Email',
      accessor: 'email',
      style: {
        width: 500,
      },
    },
    {
      Header: 'Chức quyền',
      style: {
        width: 500,
      },
      accessor: ({
        role,
      }) => ROLES[role],
    },
    {
      Header: () => (
        <Button
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => setMemberImport(true)}
          startIcon={<UilPlus />}
        >
          Thêm
        </Button>
      ),
      id: 'add',
      disableFilters: true,
      Cell: (rowProps) => {
        const rowValue = getOr({}, 'row.original', rowProps);
        return (
          <ButtonGroup>
            <IconButton
              onClick={() => setMemberUpdate(rowValue)}
              icon="edit"
              size="small"
              color="primary"
              sx={{
                marginRight: 0.5,
              }}
            >
              <UilPen />
            </IconButton>
            <Divider orientation="vertical" flexItem />
            <IconButton
              color="error"
              size="small"
              variant="outlined"
              onClick={() => setRemoveConfirm(rowValue._id)}
              sx={{
                marginLeft: 0.5,
              }}
            >
              <UilTrash />
            </IconButton>

          </ButtonGroup>
        );
      },
    },
  ], [selectedUnit]);

  const addModal = useMemo(() => (
    <MemberEditor
      title="Thêm Thành Viên"
      onClose={() => setMemberImport()}
      onHandler={(params) => {
        const _id = uuid();
        const membersUnit = cloneDeep(selectedUnit);
        const newMember = { ...params, _id };
        const valueExsit = find((value) => value?.email?.toLowerCase() === newMember?.email?.toLowerCase(), membersUnit?.members);

        if (valueExsit?.email) {
          Notification.warn('Đã tồn tại Email này trong hệ thống');
          return;
        }
        if (!membersUnit.members) { membersUnit.members = []; }
        membersUnit.members.push(newMember);
        dispatch({
          type: UNIT.handlers.update,
          data: membersUnit,
        });
        setMemberImport();
      }}
    />
  ), [memberImport]);

  const editModal = useMemo(() => (
    <MemberEditor
      onClose={() => setMemberUpdate()}
      title="Sửa Thành Viên"
      onHandler={(params) => {
        const memberUnit = keyBy('_id', cloneDeep(selectedUnit.members));
        const valueExsit = find((value) => value?.email?.toLowerCase() === params?.email?.toLowerCase(), memberUnit);
        if (valueExsit?.email && valueExsit?._id !== params?._id) {
          Notification.warn('Đã tồn tại Email này trong hệ thống');
          return;
        }
        memberUnit[params?._id] = params;
        dispatch({
          type: UNIT.handlers.update,
          data: { ...selectedUnit, members: values(memberUnit) },
        });
        setMemberUpdate();
      }}
      data={memberUpdate}
    />
  ), [memberUpdate]);

  const renderModalConfirm = useMemo(() => (
    <ConfirmDialog
      title="Bạn có chắc chắn muốn xóa?"
      color="error"
      confirmLabel="Xoá"
      cancelLabel="Huỷ"
      maxWidth="sm"
      fullWidth
      open
      onClose={() => setRemoveConfirm()}
      onConfirm={async () => {
        const newMembers = cloneDeep(selectedUnit);
        newMembers.members = filter((member) => member._id !== removeConfirm, newMembers.members);
        dispatch({
          type: UNIT.handlers.update,
          data: newMembers,
        });
        setRemoveConfirm();
      }}
    />
  ));

  return (
    <>
      { removeConfirm && renderModalConfirm}
      {handling ? <Waiting /> : null}
      {memberImport && addModal}
      {memberUpdate && editModal}
      {showImport && (
        <DropzoneModal
          onConfirm={(files) => {
            if (files.length === 0) return;
            importExcel({
              files,
              activeUnit,
              config: MEMBERS,
              onSuccess: (records) => {
                const unitData = cloneDeep(selectedUnit);
                unitData.members = concat(records, unitData.members);
                dispatch({
                  type: UNIT.handlers.update,
                  data: unitData,
                });
              },
            });
            setShowImport();
          }}
          onClose={() => setShowImport()}
        />
      )}
      <Styles.ButtonGroup>
        <Button
          startIcon={<UilUpload />}
          color="success"
          size="small"
          onClick={() => setShowImport(true)}
          sx={{
            marginRight: 1,
          }}
        >
          Nhập từ Excel
        </Button>
        <Button
          startIcon={<UilImport />}
          color="primary"
          size="small"
          onClick={() => dispatch({
            type: FILE.handlers.download,
            data: {
              unitId: activeUnit,
              fileName: 'Mau_NhanVien.xlsx',
            },
          })}
        >
          Tải file mẫu
        </Button>
      </Styles.ButtonGroup>
      <Table
        columns={columns}
        data={selectedUnit?.members}
        height={window.innerHeight - 400}
        disableGlobalFilter
        alwaysShowPagination
      />
    </>
  );
}
